import {
  ArcoCreateOrUpdateDto,
  ArcoDto,
  getGetNodoPortaleQueryKey,
  getGetNodoQueryKey,
  MiniNodoDto,
  NodoDto,
  TrasferimentoModalita,
  useGetNodo,
  useGetNodoPortale,
  useUpdateTrasferimenti,
} from "@/api";
import { useAuth } from "@/context/useAuth";
import Box from "@/elements/Box";
import Button from "@/elements/Button";
import Table from "@/elements/Table";
import { portaleConfig } from "@/portaliConfig";
import { getDispositivoTypeLabel } from "@/utils/dispositivoUtils";
import {
  IS_NEW_KEY,
  useDbRelationsUpdateForm,
} from "@/utils/useDbRelationsUpdate";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreFromTrashIcon from "@mui/icons-material/RestoreFromTrash";
import {
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import { DateTimeField } from "@mui/x-date-pickers";
import { useQueryClient } from "@tanstack/react-query";
import { ColumnDef, createColumnHelper, Row } from "@tanstack/react-table";
import moment from "moment";
import { useCallback, useMemo } from "react";
import { Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { withOpeningReset } from "../../utils/withOpeningReset";
import AziendaPicker from "../AziendaPicker";
import { SmallLoading } from "../Loading";
import { NodoPicker } from "../NodoPicker";

const columnHelper = createColumnHelper<ArcoCreateOrUpdateDto>();
function TrasferimentiDialog({
  open = false,
  canEdit = false,
  onClose,
  nodo: _nodo,
}: {
  open?: boolean;
  canEdit?: boolean;
  onClose?: () => void;
  nodo?: NodoDto;
}) {
  const { data: fullNodoData } = useGetNodoPortale(
    portaleConfig.id,
    _nodo?.id ?? 0,
    {
      query: {
        enabled: !!_nodo,
      },
    }
  );

  const nodo = fullNodoData?.data;

  const dispositivoLabel = nodo
    ? `${getDispositivoTypeLabel(nodo?.dispositivoModello!.tipoDispositivo!)} ${
        nodo?.dispositivoMatricola
      }`
    : "";

  const { t } = useTranslation();

  const handleClose = () => {
    onClose?.();
  };

  const editing = canEdit;

  const initialTrasferimenti = useMemo(() => {
    return (
      nodo?.trasferimentiBreadcrumbs?.map(
        (breadcrumbs) => breadcrumbs[breadcrumbs.length - 1].arco
      ) ?? []
    );
  }, [nodo?.trasferimentiBreadcrumbs]);

  const {
    data,
    control,
    resetField,
    watch,
    handleSubmit,
    getChangeSubmitData,
    create,
    remove,
    restore,
    getFieldName,
    setValue,
    getEntityState,
  } = useDbRelationsUpdateForm<ArcoCreateOrUpdateDto>(initialTrasferimenti, {
    deleteChangeType: "deleted",
  });

  const { hasPermission } = useAuth();

  const columns = useMemo<ColumnDef<ArcoCreateOrUpdateDto, any>[]>(() => {
    console.log("rerunning memo");
    const editingDataTimeCell = (
      _: string | null | undefined,
      name: ReturnType<typeof getFieldName>
    ) => {
      return (
        <Controller
          name={name as any}
          control={control}
          render={({ field }) => {
            const { value, name, onBlur, onChange, ref } = field;
            const m =
              typeof value === "string"
                ? moment(value)
                : moment.isMoment(value)
                ? value
                : null;
            return (
              <DateTimeField
                ref={ref}
                format="DD/MM/YYYY HH:mm"
                name={name}
                onBlur={onBlur}
                onChange={(value) => onChange(value?.toISOString())}
                value={m}
              />
            );
          }}
        />
      );
    };
    const readonlyDataTimeCell = (value: string | null | undefined) => {
      const m = value ? moment(value) : null;
      return m ? m.format("L LT") : "";
    };

    const dataTimeCell = editing ? editingDataTimeCell : readonlyDataTimeCell;

    const columns = [
      columnHelper.accessor("titolareId", {
        header: editing
          ? t("trasferimentiDialog.colonne.proprietarioObbligatorio")
          : t("trasferimentiDialog.colonne.proprietario"),
        size: 175,
        cell: editing
          ? ({ row }) => (
              <Box display="flex" flexDirection="column" rowGap={1}>
                <Controller
                  name={getFieldName(row.index, "titolareId")}
                  control={control}
                  render={({ field }) => {
                    return (
                      <AziendaPicker
                        {...field}
                        type="aziende"
                        value={
                          field.value ? ({ id: field.value } as any) : null
                        }
                        onChange={(e, v) => {
                          field.onChange(v?.id ?? null);
                        }}
                      />
                    );
                  }}
                />
                <Controller
                  name={getFieldName(row.index, "trasferimentoModalita")}
                  control={control}
                  render={({ field }) => {
                    return (
                      <Autocomplete<TrasferimentoModalita>
                        onChange={(e, value) => {
                          field.onChange(value);
                        }}
                        options={Object.values(TrasferimentoModalita)}
                        getOptionLabel={(trasferimento) => {
                          return `${trasferimento}`;
                        }}
                        isOptionEqualToValue={(option, value) =>
                          option === value
                        }
                        value={field.value}
                        renderInput={(params) => (
                          <TextField {...params} value={field.value} />
                        )}
                        size="medium"
                      />
                    );
                  }}
                />
              </Box>
            )
          : ({ row }) => (row.original as ArcoDto).titolare?.nome ?? "",
      }),
      columnHelper.accessor("da", {
        header: t("trasferimentiDialog.colonne.da"),
        size: 100,
        maxSize: 100,
        cell: ({ getValue, row }) =>
          dataTimeCell(getValue(), getFieldName(row.index, "da")),
      }),
      columnHelper.accessor("a", {
        header: t("trasferimentiDialog.colonne.a"),
        size: 100,
        maxSize: 100,
        cell: ({ getValue, row }) =>
          dataTimeCell(getValue(), getFieldName(row.index, "a")),
      }),
      columnHelper.display({
        header: t("trasferimentiDialog.colonne.posizione"),
        cell: ({ row }) => {
          return (
            <Controller
              control={control}
              name={getFieldName(row.index, "sorgenteId")}
              render={({ field }) => {
                return (
                  <PosizioneCell
                    nodoId={field.value}
                    onChange={field.onChange}
                    onChangeSorgenteAziendaId={(x) => {
                      setValue(
                        getFieldName(row.index, "sorgenteAziendaId"),
                        x ?? undefined
                      );
                    }}
                    editing={editing}
                  />
                );
              }}
            />
          );
        },
      }),
      editing &&
        columnHelper.display({
          id: "buttons",
          header: "",
          size: 30,
          cell: ({ row }) => {
            return (
              <>
                <Box display="flex" gap={1}>
                  <IconButton
                    title={t("azioni.elimina")}
                    size="small"
                    color="error"
                    onClick={() => {
                      remove(row.index);
                    }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <Box
                  display="flex"
                  gap={1}
                  className="deleted-row-visible-content"
                >
                  <IconButton
                    title={t("azioni.ripristina")}
                    size="small"
                    onClick={() => {
                      restore(row.index);
                    }}
                  >
                    <RestoreFromTrashIcon />
                  </IconButton>
                </Box>
              </>
            );
          },
        }),
    ].filter((x) => x);
    return columns as Exclude<(typeof columns)[number], boolean>[];
  }, [
    editing,
    control,
    getFieldName,
    hasPermission,
    resetField,
    watch,
    remove,
    restore,
    t,
  ]);

  const isRowDeleted = useCallback(
    (row: Row<ArcoCreateOrUpdateDto>) => {
      return getEntityState(row.index) === "deleted";
    },
    [getEntityState]
  );

  const { mutateAsync, isLoading, error } = useUpdateTrasferimenti({
    mutation: {
      onSuccess: () => {
        toast.success(t("trasferimentiDialog.toast.salvati"));
      },
      onError: () => {
        toast.error(t("trasferimentiDialog.toast.errore"));
      },
    },
  });

  const queryClient = useQueryClient();

  const onSubmit = async () => {
    if (!nodo) {
      return;
    }

    const data = getChangeSubmitData();
    console.log("change data:", data);
    try {
      await mutateAsync({
        id: nodo?.id ?? 0,
        data: data.map((change) => {
          if (change.changeType === "added") {
            change.entity.destinazioneId = nodo.id;
          }
          return change;
        }),
      });

      queryClient.invalidateQueries({
        queryKey: getGetNodoQueryKey(nodo!.id),
      });
      queryClient.invalidateQueries({
        queryKey: getGetNodoPortaleQueryKey(portaleConfig.id, nodo!.id),
      });
      onClose?.();
    } catch (err) {}
  };

  const isValid = useMemo(() => {
    const { data } = watch();
    return data.every((x: ArcoCreateOrUpdateDto) => {
      return x.titolareId;
    });
  }, [watch()]);

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth={false}>
      <DialogTitle>
        {t("trasferimentiDialog.titolo", { dispositivo: dispositivoLabel })}
      </DialogTitle>
      <DialogContent>
        <Box>
          <Box>
            {editing && (
              <Button
                size="small"
                color="light"
                onClick={() => {
                  create();
                }}
              >
                {t("trasferimentiDialog.azioni.aggiungi")}
              </Button>
            )}
          </Box>
          <Table
            columns={columns}
            data={data}
            sortable={false}
            initialSort={[
              { id: IS_NEW_KEY, desc: false },
              { id: "da", desc: true },
            ]}
            isRowDeleted={isRowDeleted}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        {isLoading && <SmallLoading sx={{ mr: 1 }} />}
        {editing && (
          <Button
            size="small"
            color="primary"
            onClick={handleSubmit(onSubmit)}
            disabled={!isValid}
          >
            {t("azioni.salva")}
          </Button>
        )}
        {onClose && (
          <Button size="small" color="light" onClick={handleClose}>
            {t("azioni.chiudi")}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function PosizioneCell({
  nodoId,
  onChange,
  onChangeSorgenteAziendaId,
  editing
}: {
  nodoId: number | null | undefined;
  onChange: (x: number | null) => void;
  onChangeSorgenteAziendaId: (x: number | null) => void;
  editing: boolean;
}) {
  const { data: nodo, isLoading } = useGetNodo(nodoId ?? 0, {
    query: { enabled: !!nodoId },
  });

  return (
    <NodoPicker
      grow="vertically"
      value={nodo?.data ?? null}
      readOnly={!editing}
      onChange={(newNodo) => {
        if (newNodo) {
          const path: (NodoDto | MiniNodoDto)[] = [
            ...(newNodo.breadcrumbs?.map((x) => x.nodo) ?? []),
            newNodo,
          ];
          onChange(newNodo.id);
          const azienda = path[0];
          if (azienda.tipo === "Azienda") {
            onChangeSorgenteAziendaId(azienda.id);
          }
        } else {
          onChange(null);
          onChangeSorgenteAziendaId(null);
        }
      }}
    />
  );
}

export default withOpeningReset(TrasferimentiDialog);
